/* eslint-disable @typescript-eslint/naming-convention */
import { Button, Grid, Typography, CircularProgress, IconButton } from '@quillbot/design-system';
import {
  UNITS,
  w100p,
  unitPX,
  fontSize,
  fontWeight,
  displayFlex,
  displayNone,
  pointerEventsNone,
  center,
} from 'src/researcher/styles';
import { ArrowLeftIcon, HistoryIcon, TrashIcon } from 'src/researcher/component/icons';
import { deleteProjectAPI, undeleteProjectAPI } from 'src/researcher/handlers/api';
import { openToastr } from '@Common/handlers/services';
import {
  DeleteForeverButtonTooltip,
  ErrorDeletingProjectMsg,
  ErrorUnDeletingProject,
  RestoreButtonTooltip,
  NoProjectFoundTrashView,
} from 'src/researcher/constants/message.constants';
import { useRouter } from 'next/router';
import { getDisplayDateAndTime, truncateStringAfter } from 'src/researcher/utils';
import { memo, useMemo } from 'react';
import { AsyncStatusEnum } from 'src/researcher/types/common';
import { IsVisible } from 'src/researcher/component/common/IsVisible';
import researcherTrack from 'src/researcher/handlers/track';
import { useSliderVisibility } from './Slider';
import { pageStyles } from './styles.css';
import { CardIdProvider, KebabProvider, useCardId } from './Card.provider';
import { CardLayout } from './Card.layout';
import { useRecentTrashProjectDispatch, useRecentTrashProjectState } from './list.store';
import {
  deleteProjectFromListAction,
  restoreProjectFromListAction,
  setDeletingProjectCardListStoreAction,
} from './list.reducer';
import { Tooltip } from '../common';
import { CardInfo } from './TrashCard.layout';

export const async_loader_class = 'async_loader_container';
export const control_buttons_class = 'control_buttons';

export const controlButtonStyles = { p: unitPX(UNITS[0]) };

export const loaderStyles = {
  width: unitPX(UNITS['3pt']),
  height: unitPX(UNITS['3pt']),
};

const styles = {
  card: {
    boxSizing: 'border-box',
    [`& .${async_loader_class}`]: displayFlex,
    [`& .${control_buttons_class}`]: {
      ...displayNone,
      ...pointerEventsNone,
    },
  },
};

const SIZE = {
  WIDTH: UNITS['26pt'],
  HEIGHT: UNITS['31pt'],
};

function CardControlButtonInDelete() {
  const dispatch = useRecentTrashProjectDispatch();
  const id = useCardId();
  const onRestoreClick = () => {
    // un-deleted api
    // if project is deleted isDeleted true then mark isDeleted false
    researcherTrack.restoredProject();
    dispatch(restoreProjectFromListAction({ id, restoring: true, context: 'trash' }));
    undeleteProjectAPI(id)
      .then(() => dispatch(deleteProjectFromListAction(id, 'trash', true)))
      .catch(() => openToastr(ErrorUnDeletingProject))
      .finally(() =>
        dispatch(restoreProjectFromListAction({ id, restoring: false, context: 'trash' }))
      );
  };
  const onDeleteClick = () => {
    // delete permanently
    // see if project already deleted if yes then delete permanently
    researcherTrack.deletedProject('Permanent', 'Trash View');
    dispatch(setDeletingProjectCardListStoreAction({ id, deleting: true, context: 'trash' }));
    deleteProjectAPI(id)
      .then(() => dispatch(deleteProjectFromListAction(id, 'trash', false)))
      .catch(() => openToastr(ErrorDeletingProjectMsg))
      .finally(() =>
        dispatch(setDeletingProjectCardListStoreAction({ id, deleting: false, context: 'trash' }))
      );
  };

  return (
    <Grid flex={1} container justifyContent="flex-end">
      <Grid item display="none" className={async_loader_class} pr={unitPX(UNITS.pt)}>
        <CircularProgress size="small" sx={loaderStyles} />
      </Grid>
      <Grid item className={control_buttons_class}>
        <Tooltip arrow title={RestoreButtonTooltip}>
          <span>
            <IconButton
              onClick={onRestoreClick}
              sx={[controlButtonStyles, { mr: unitPX(UNITS['0.5pt']) }]}
            >
              <HistoryIcon sx={{ width: unitPX(UNITS['2.5pt']), height: unitPX(UNITS['2.5pt']) }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip arrow title={DeleteForeverButtonTooltip}>
          <span>
            <IconButton onClick={onDeleteClick} sx={controlButtonStyles}>
              <TrashIcon sx={{ width: unitPX(UNITS['2.5pt']), height: unitPX(UNITS['2.5pt']) }} />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
    </Grid>
  );
}

function CardComponent() {
  const router = useRouter();

  const id = useCardId();
  const title = useRecentTrashProjectState((state) => state.trash.projects[id].title);
  const preview = useRecentTrashProjectState((state) => state.trash.projects[id].preview);
  const time = useRecentTrashProjectState((state) =>
    getDisplayDateAndTime(state.trash.projects[id].updatedAt || state.trash.projects[id].createdAt)
  );

  const controls = useRecentTrashProjectState((state) =>
    state.trash.projects[id].deleting || state.trash.projects[id].restoring ? 'card-mount' : 'hover'
  );

  const sx = useRecentTrashProjectState((state) =>
    state.trash.projects[id].deleting || state.trash.projects[id].restoring ? styles.card : null
  );

  return (
    <CardLayout {...SIZE} sx={sx} controls={controls}>
      <CardInfo time={time} title={truncateStringAfter(title, 19)} preview={preview}>
        <CardControlButtonInDelete />
      </CardInfo>
    </CardLayout>
  );
}

const Card = memo(CardComponent);

function Loader() {
  const isLoading = useRecentTrashProjectState(
    (state) => state.trash.status === AsyncStatusEnum.PENDING
  );

  const hasNoProjects = useRecentTrashProjectState((state) => state.recent.count === 0);

  return (
    <IsVisible
      visible={isLoading}
      sx={[{ pt: unitPX(hasNoProjects ? UNITS['10pt'] : UNITS['2pt']) }]}
    >
      <CircularProgress
        size="small"
        sx={{ width: unitPX(UNITS['3pt']), height: unitPX(UNITS['3pt']) }}
      />
    </IsVisible>
  );
}

export function DeleteView() {
  const [o, setSliderVisible] = useSliderVisibility();
  const projectsCollection = useRecentTrashProjectState((state) => state.trash.projects);
  const projectIds = useMemo(() => Object.keys(projectsCollection), [projectsCollection]);

  return (
    <Grid container item xs={12} flex={1}>
      <Grid
        item
        container
        xs={12}
        justifyContent="center"
        sx={[pageStyles.py, pageStyles.headerBg, pageStyles.px]}
      >
        <Grid container item justifyContent="space-between" sx={[w100p, pageStyles.maxPageWidth]}>
          <Grid item xs={4}>
            <Button
              variant="text"
              startIcon={<ArrowLeftIcon />}
              onClick={() => setSliderVisible('none')}
            >
              Back
            </Button>
          </Grid>
          <Grid item xs={3}>
            <Grid container pl={unitPX(UNITS['4pt'])} alignContent="center" justifyContent="center">
              <Typography sx={[fontSize[20], fontWeight[400]]}>Trashed Projects</Typography>
            </Grid>
          </Grid>
          <Grid item xs={5} />
        </Grid>
      </Grid>
      {projectIds.length ? (
        <Grid
          container
          item
          xs={12}
          flex={1}
          justifyContent="center"
          sx={[pageStyles.py, pageStyles.listBg, pageStyles.px]}
        >
          <Grid container item flex={1} sx={[pageStyles.maxPageWidth]}>
            <Grid flex={1} container rowSpacing={14} columnSpacing={24}>
              {projectIds.map((id) => (
                <CardIdProvider key={id} id={id}>
                  <KebabProvider>
                    {/* this Grid.item is required for spacing */}
                    <Card />
                  </KebabProvider>
                </CardIdProvider>
              ))}
            </Grid>
            <Grid container item xs={12} justifyContent="center" alignItems="center">
              <Loader />
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid
          item
          xs={12}
          style={{ paddingTop: unitPX(UNITS['14pt']) }}
          px={unitPX(UNITS['1.5pt'])}
          mt={unitPX(UNITS['2.5pt'])}
          sx={[displayFlex, center]}
        >
          <Typography sx={[fontSize[16], fontWeight[600], { color: '#686868' }]}>
            {NoProjectFoundTrashView}
          </Typography>
        </Grid>
        // <Grid container alignContent="center" justifyContent="center" height="100%">
        // <Typography>{NoProjectFoundTrashView}</Typography>
        // </Grid>
      )}
    </Grid>
  );
}
